import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { SearchView } from "@shared/site-audit-report-view";
import { competitorShare, storedCompetitorFigure } from "./competitor-share";
import { PresentedCopy } from "./presented-text";
import { ReportSection } from "./section";

export function SearchSection({ search }: { search: SearchView }) {
  const chartRows = search.competitors.map((row) => ({
    domain: row.domain,
    Authority: competitorShare(row.authority, search.competitors.map((item) => item.authority)),
    Traffic: competitorShare(row.traffic, search.competitors.map((item) => item.traffic)),
    Keywords: competitorShare(row.keywords, search.competitors.map((item) => item.keywords)),
  }));
  const chartSummary = search.competitors
    .map((row) => `${row.domain}: authority ${row.authorityLabel}, traffic ${row.trafficLabel}, keywords ${row.keywordsLabel}`)
    .join(". ");

  return (
    <ReportSection id="search" title="Search" hint="Showing up to 10 keywords and gap rows.">
      {search.keywords.length === 0 ? (
        <p className="text-sm text-foreground/75">{search.keywordEmpty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[32rem] text-left text-sm">
            <caption className="sr-only">Top keywords. Showing {search.keywords.length} of {search.keywordTotal}.</caption>
            <thead>
              <tr className="border-b border-border text-xs text-foreground/75">
                <th className="py-2 pr-3 font-medium">Keyword</th>
                <th className="py-2 pr-3 font-medium">Position</th>
                <th className="py-2 pr-3 font-medium">Volume</th>
                <th className="py-2 font-medium">Traffic</th>
              </tr>
            </thead>
            <tbody>
              {search.keywords.map((row, index) => (
                <tr key={`keyword-${index}`} className="border-b border-border/70">
                  <td className="py-2 pr-3">
                    <p>{row.keyword}</p>
                    {row.url ? <p className="truncate font-mono text-xs text-foreground/75">{row.url}</p> : null}
                  </td>
                  <td className="py-2 pr-3 tabular-nums">
                    <span className="mr-2 inline-block h-1.5 rounded-full bg-foreground/50 align-middle" style={{ width: `${Math.round((row.positionBar ?? 0) * 48)}px` }} />
                    {row.positionLabel}
                  </td>
                  <td className="py-2 pr-3 tabular-nums">{row.volume}</td>
                  <td className="py-2 tabular-nums">{row.traffic}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {search.keywordTotal > search.keywords.length ? (
            <p className="mt-2 text-xs text-foreground/75">Showing {search.keywords.length} of {search.keywordTotal}.</p>
          ) : null}
        </div>
      )}

      {search.competitors.length === 0 ? (
        <p className="text-sm text-foreground/75">{search.competitorEmpty}</p>
      ) : (
        <div className="space-y-3">
          <div className="h-60 w-full" aria-hidden="true">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartRows} barGap={2}>
                <XAxis dataKey="domain" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                <Tooltip
                  formatter={(value, name, item) => {
                    const payload = item && typeof item === "object" && "payload" in item ? item.payload : null;
                    const domain = payload && typeof payload === "object" && payload && "domain" in payload ? String(payload.domain) : "";
                    const stored = storedCompetitorFigure(String(name), domain, search.competitors);
                    return [stored ?? String(value), name];
                  }}
                />
                <Legend />
                <Bar dataKey="Authority" fill="var(--chart-2)" isAnimationActive={false} />
                <Bar dataKey="Traffic" fill="var(--chart-3)" isAnimationActive={false} />
                <Bar dataKey="Keywords" fill="var(--chart-4)" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="sr-only">{chartSummary}</p>
          <p className="text-xs text-foreground/75">Each bar is that figure as a share of the highest value in the column. The table has the stored numbers.</p>
          {search.competitorEmpty ? <p className="text-sm text-foreground/75">{search.competitorEmpty}</p> : null}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] text-left text-sm">
              <caption className="sr-only">Competitor figures</caption>
              <thead>
                <tr className="border-b border-border text-xs text-foreground/75">
                  <th className="py-2 pr-3 font-medium">Domain</th>
                  <th className="py-2 pr-3 font-medium">Authority</th>
                  <th className="py-2 pr-3 font-medium">Traffic</th>
                  <th className="py-2 pr-3 font-medium">Keywords</th>
                  <th className="py-2 font-medium">In common</th>
                </tr>
              </thead>
              <tbody>
                {search.competitors.map((row) => (
                  <tr key={row.domain} className="border-b border-border/70">
                    <td className="py-2 pr-3">{row.prospect ? `${row.domain} (this site)` : row.domain}</td>
                    <td className="py-2 pr-3 tabular-nums">{row.authorityLabel}</td>
                    <td className="py-2 pr-3 tabular-nums">{row.trafficLabel}</td>
                    <td className="py-2 pr-3 tabular-nums">{row.keywordsLabel}</td>
                    <td className="py-2 tabular-nums">{row.commonKeywordsLabel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {search.competitiveAnalysis ? (
        <PresentedCopy value={search.competitiveAnalysis} className="max-w-3xl text-sm leading-relaxed whitespace-pre-line" />
      ) : null}

      {search.gaps.length === 0 ? (
        <p className="text-sm text-foreground/75">{search.gapEmpty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[32rem] text-left text-sm">
            <caption className="sr-only">Keyword gap. Showing {search.gaps.length} of {search.gapTotal}.</caption>
            <thead>
              <tr className="border-b border-border text-xs text-foreground/75">
                <th className="py-2 pr-3 font-medium">Keyword</th>
                <th className="py-2 pr-3 font-medium">Volume</th>
                <th className="py-2 pr-3 font-medium">Competitor</th>
                <th className="py-2 font-medium">Their position</th>
              </tr>
            </thead>
            <tbody>
              {search.gaps.map((row, index) => (
                <tr key={`gap-${index}`} className="border-b border-border/70">
                  <td className="py-2 pr-3">{row.keyword}</td>
                  <td className="py-2 pr-3 tabular-nums">{row.volume}</td>
                  <td className="py-2 pr-3">{row.competitor}</td>
                  <td className="py-2 tabular-nums">{row.position}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ReportSection>
  );
}
